/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FunctionComponent, MouseEvent, useEffect, useRef, useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { isFunction, isUndefined } from "@adapptio/lib-shared";
import { useBackdropClick } from "@adapptio/lib-editor-ui";
import { Icon, ICON_SIZE } from "./Icon";
import { FC_ANCHOR, FC_DIRECTION, FC_POSITION, FC_SIZE, FloatingContainer } from "./FloatingContainer";
import { useBodyClick } from "../hooks/useBodyClick";
import { useEscKey } from "../hooks/useEscKey";
import { FadeInOut } from "./FadeInOut";

/**
 * References to opened menus by ID
 * Used to close previously opened menu with the same ID
 */
const openedMenuRefs: {
	[K: string]: {
		uid: number;
		onClose: () => void;
	};
} = {};

let lastMenuId = 0;

/**
 * Dropdown Menu Item
 */
export interface IDropdownMenuItem {
	id: string;
	label: string;
	icon?: string;
	title?: string;
	disabled?: boolean;
	selected?: boolean;
	onClick?: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
}

export enum DROPDOWN_STYLE {
	DEFAULT = "default",
	COMPACT = "compact"
}

/**
 * Downdown Menu props
 */
export interface IDropdownMenuProps {
	parent: React.MutableRefObject<HTMLElement>;
	items: IDropdownMenuItem[];
	selectedItemId?: string;
	isOpened: boolean;
	onItemClick?: (item: IDropdownMenuItem) => void;
	onClose?: () => void;
	anchor?: FC_ANCHOR;
	direction?: FC_DIRECTION;
	size?: FC_SIZE;
	position?: FC_POSITION;
	offset?: number;
	style?: DROPDOWN_STYLE;
	className?: string;
	id?: string;
}

/**
 * Action Button component
 */
export const DropdownMenu: FunctionComponent<IDropdownMenuProps> = ({
	parent,
	items,
	selectedItemId,
	isOpened,
	onItemClick,
	onClose,
	anchor: menuAnchor,
	direction: menuDirection,
	size: menuSize,
	position: menuPosition,
	offset: menuOffset,
	style,
	className,
	id
}) => {
	const panelRef = useRef();
	const _id = id ? id : "_";
	const [ _uid ] = useState(() => lastMenuId++);

	useBodyClick(() => {
		if (onClose) {
			onClose();
		}
	}, isOpened);

	useEscKey(() => {
		if (onClose) {
			onClose();
		}
	}, isOpened);

	useEffect(() => {
		if (isOpened) {
			// Close prev menu instance
			if (openedMenuRefs[_id] && openedMenuRefs[_id].uid !== _uid) {
				openedMenuRefs[_id].onClose();
			}
			// Assign self as new instance
			openedMenuRefs[_id] = {
				uid: _uid,
				onClose: onClose
			};
		} else if (openedMenuRefs[_id]?.uid === _uid) {
			delete openedMenuRefs[_id];
		}

		return () => {
			if (openedMenuRefs[_id]?.uid === _uid) {
				delete openedMenuRefs[_id];
			}
		};
	}, [ isOpened, onClose, _id ]);

	useBackdropClick(
		(event) => {
			if (isOpened && isFunction(onClose)) {
				event.preventDefault();
				event.stopPropagation();
				onClose();
			}
		},
		[ panelRef, parent ],
		isOpened
	);

	return (
		<FadeInOut
			isVisible={isOpened}
			fadeOutDelay={250}
			render={(isVisible) => {
				const baseClass = BEM("edt-dropdown-menu", null, {
					"is-visible": isVisible
				});

				baseClass.mod(`style-${style || DROPDOWN_STYLE.DEFAULT}`);

				if (className) {
					baseClass.add(className);
				}

				return (
					<FloatingContainer
						containerRef={panelRef}
						parent={parent}
						anchor={menuAnchor || FC_ANCHOR.RIGHT_BOTTOM}
						direction={menuDirection || FC_DIRECTION.HR}
						size={menuSize || FC_SIZE.FIT_MIN}
						position={menuPosition || FC_POSITION.AUTO}
						offset={menuOffset}
						className={baseClass.toClassName()}
					>
						<ul className={baseClass.element("wrapper").toClassName()} onClick={onClose}>
							{items.map((item) => {
								const selected = isUndefined(selectedItemId)
									? !!item.selected
									: selectedItemId === item.id;

								const itemClass = baseClass.element("item", {
									"is-selected": selected,
									disabled: item.disabled
								});

								const handleMenuItemClick = (ev: MouseEvent<HTMLButtonElement>) => {
									ev.stopPropagation();

									if (item.onClick) {
										item.onClick(ev);
									}

									if (onItemClick) {
										onItemClick(item);
									}
								};

								return (
									<li key={item.id} className={itemClass.toClassName()}>
										<button
											title={item.title}
											onClick={handleMenuItemClick}
											disabled={item.disabled}
										>
											{item.icon ? (
												<Icon name={item.icon} size={ICON_SIZE.CUSTOM} />
											) : null}
											<span className={baseClass.element("item-label").toClassName()}>
												{item.label}
											</span>
										</button>
									</li>
								);
							})}
						</ul>
					</FloatingContainer>
				);
			}}
		/>
	);
};
